import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { usePermissions } from "@/hooks/use-permissions";
import { PermissionLock, PermissionLockLoading } from "@/components/admin/PermissionLock";
import { CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Rocket, ClipboardList, Eye, MessageSquare } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/launch")({
  component: LaunchChecklistPage,
});

type Counters = {
  publishedStories: number;
  publishedTotal: number;
  draftTotal: number;
  missingCovers: number;
  missingSlug: number;
  missingReadingMinutes: number;
  missingCharacters: number;
  newFeedback: number;
};

function LaunchChecklistPage() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const perms = usePermissions();
  const [counts, setCounts] = useState<Counters | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (perms.loading || !perms.canManageContent) return;
    (async () => {
      setLoading(true);
      const [pubStories, pubAll, drafts, missingCov, missingSlug, missingMin, allPub, links, newFb] = await Promise.all([
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("kind", "story").eq("status", "published"),
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("status", "published"),
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("status", "draft"),
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("status", "published").is("cover_url", null),
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("status", "published").is("slug", null),
        supabase.from("content_items").select("id", { count: "exact", head: true }).eq("status", "published").is("reading_minutes", null),
        supabase.from("content_items").select("id").eq("status", "published"),
        supabase.from("content_characters").select("content_id"),
        supabase.from("beta_feedback").select("id", { count: "exact", head: true }).eq("status", "new"),
      ]);

      const linkedIds = new Set((links.data ?? []).map((r) => r.content_id));
      const missingChars = (allPub.data ?? []).filter((r) => !linkedIds.has(r.id)).length;

      setCounts({
        publishedStories: pubStories.count ?? 0,
        publishedTotal: pubAll.count ?? 0,
        draftTotal: drafts.count ?? 0,
        missingCovers: missingCov.count ?? 0,
        missingSlug: missingSlug.count ?? 0,
        missingReadingMinutes: missingMin.count ?? 0,
        missingCharacters: missingChars,
        newFeedback: newFb.count ?? 0,
      });
      setLoading(false);
    })();
  }, [perms.loading, perms.canManageContent]);

  const checks = useMemo(() => {
    if (!counts) return [];
    return [
      { ok: counts.publishedStories >= 7, label: isAr ? "٧ قصص منشورة على الأقل" : "≥ 7 published stories", detail: `${counts.publishedStories} / 7` },
      { ok: counts.missingCovers === 0, label: isAr ? "كل القصص فيها غلاف" : "All published items have covers", detail: counts.missingCovers === 0 ? (isAr ? "تمام" : "OK") : `${counts.missingCovers} ${isAr ? "بدون غلاف" : "missing"}` },
      { ok: counts.missingSlug === 0, label: isAr ? "كل المنشور فيه slug" : "All published items have a slug", detail: counts.missingSlug === 0 ? (isAr ? "تمام" : "OK") : `${counts.missingSlug} ${isAr ? "ناقص" : "missing"}` },
      { ok: counts.missingReadingMinutes === 0, label: isAr ? "زمن القراءة محدد" : "Reading minutes set", detail: counts.missingReadingMinutes === 0 ? (isAr ? "تمام" : "OK") : `${counts.missingReadingMinutes} ${isAr ? "ناقص" : "missing"}` },
      { ok: counts.missingCharacters === 0, label: isAr ? "كل المنشور مربوط بشخصية" : "All published items linked to a character", detail: counts.missingCharacters === 0 ? (isAr ? "تمام" : "OK") : `${counts.missingCharacters} ${isAr ? "غير مربوط" : "unlinked"}` },
    ];
  }, [counts, isAr]);

  if (perms.loading) return <PermissionLockLoading />;
  if (!perms.canManageContent) {
    return <PermissionLock titleEn="Admin only" titleAr="وصول محظور" messageEn="Content managers only." messageAr="هذه الصفحة للمشرفين فقط." />;
  }

  return (
    <div dir={dir} className="px-5 py-7 max-w-3xl mx-auto pb-16">
      <Link to="/app/admin" className="inline-flex items-center gap-1 text-xs text-foreground/60 hover:text-primary mb-4">
        <ArrowLeft className="size-3.5" />
        {isAr ? "العودة للإدارة" : "Back to admin"}
      </Link>

      <div className="mb-6">
        <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-1 inline-flex items-center gap-1.5">
          <Rocket className="size-3.5" />
          {isAr ? "جاهزية الإطلاق" : "Launch readiness"}
        </p>
        <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl font-bold tracking-tight`}>
          {isAr ? "قائمة الإطلاق والبيتا" : "Launch & Beta Checklist"}
        </h1>
        <p className="text-sm text-foreground/60 mt-1">
          {isAr ? "تحقق سريع من المحتوى وتجربة الأهل قبل دعوة المختبرين." : "Quick health check before inviting beta cohorts."}
        </p>
      </div>

      {/* Live counters */}
      {loading || !counts ? (
        <div className="flex justify-center py-12">
          <div className="size-8 rounded-full border-4 border-clay border-t-primary animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          <Stat label={isAr ? "قصص منشورة" : "Stories live"} value={counts.publishedStories} good={counts.publishedStories >= 7} />
          <Stat label={isAr ? "كل المنشور" : "Published total"} value={counts.publishedTotal} />
          <Stat label={isAr ? "مسودات" : "Drafts"} value={counts.draftTotal} />
          <Stat label={isAr ? "ملاحظات جديدة" : "New feedback"} value={counts.newFeedback} highlight={counts.newFeedback > 0} />
        </div>
      )}

      {/* Content checklist */}
      <section className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-widest text-foreground/60 mb-3">
          {isAr ? "صحة المحتوى" : "Content health"}
        </h2>
        <div className="rounded-3xl bg-card border-2 border-clay/40 divide-y divide-clay/30">
          {checks.map((c, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              {c.ok ? (
                <CheckCircle2 className="size-5 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="size-5 text-amber-600 shrink-0" />
              )}
              <div className="flex-1 min-w-1">
                <p className="text-sm font-medium">{c.label}</p>
              </div>
              <span className={`text-xs font-bold ${c.ok ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                {c.detail}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Onboarding observation checklist (static) */}
      <section className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-widest text-foreground/60 mb-3 inline-flex items-center gap-1.5">
          <ClipboardList className="size-4" />
          {isAr ? "ملاحظة جلسة الأهل (٥ دقائق)" : "Parent onboarding observation (5 min)"}
        </h2>
        <ul className="rounded-3xl bg-card border-2 border-clay/40 px-4 py-3 space-y-2 text-sm">
          {(isAr
            ? [
                "هل وصل الأب/الأم للتسجيل خلال ٣٠ ثانية؟",
                "هل فهم لماذا نطلب اسم الطفل وعمره؟",
                "هل ظهرت اللغة الصحيحة (عربي/إنجليزي) تلقائياً؟",
                "هل أنشأ ملف الطفل بدون تردد؟",
                "هل ظهرت قصة أولى مناسبة لعمر الطفل؟",
                "هل قرأ الطفل/الأهل القصة حتى النهاية؟",
                "هل ظهرت لحظة مكافأة/ابتسامة عند نهاية القصة؟",
                "هل عرف الأب كيف يضبط رمز PIN؟",
                "هل وجد كيف يرسل ملاحظة من داخل التطبيق؟",
              ]
            : [
                "Did the parent reach signup within 30s?",
                "Did they understand why we ask for child age?",
                "Did the correct language (AR/EN) show automatically?",
                "Was creating the child profile friction-free?",
                "Did a first story appropriate for the child's age appear?",
                "Did the child/parent finish reading it?",
                "Was there a delight moment at the end?",
                "Did the parent find how to set the PIN?",
                "Did they find the in-app feedback button?",
              ]
          ).map((q, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 inline-block size-4 rounded border-2 border-clay/60 shrink-0" />
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Reviewer workflow notes */}
      <section className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-widest text-foreground/60 mb-3 inline-flex items-center gap-1.5">
          <Eye className="size-4" />
          {isAr ? "سير عمل المراجع" : "Reviewer workflow"}
        </h2>
        <ol className="rounded-3xl bg-card border-2 border-clay/40 px-4 py-3 space-y-2 text-sm list-decimal list-inside marker:text-primary marker:font-bold">
          {(isAr
            ? [
                "افتح طابور المراجعة وراجع المسودات.",
                "تحقق: عنوان عربي + إنجليزي، نص متّسق، عمر مناسب.",
                "تأكد من وجود غلاف، slug، زمن قراءة، شخصية مرتبطة.",
                "إذا كان كل شيء سليم، اضغط «انشر».",
                "افتح ملاحظات البيتا وحدّث الحالة (قيد العمل / تم).",
              ]
            : [
                "Open the review queue and pick the oldest draft.",
                "Check: AR + EN titles, body parity, age fit.",
                "Confirm cover, slug, reading minutes, character link.",
                "If clean, hit Publish.",
                "Open Beta feedback and triage new items (in-progress / done).",
              ]
          ).map((step, i) => (
            <li key={i} className="ms-2">{step}</li>
          ))}
        </ol>
      </section>

      {/* Quick jumps */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <QuickLink to="/app/admin/review" icon={<Eye className="size-5" />} label={isAr ? "طابور المراجعة" : "Review queue"} />
        <QuickLink to="/app/admin/feedback" icon={<MessageSquare className="size-5" />} label={isAr ? "ملاحظات البيتا" : "Beta feedback"} badge={counts?.newFeedback} />
        <QuickLink to="/app/admin" icon={<ClipboardList className="size-5" />} label={isAr ? "إدارة المحتوى" : "Content studio"} />
      </div>
    </div>
  );
}

function Stat({ label, value, good, highlight }: { label: string; value: number; good?: boolean; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-3 border-2 ${good ? "bg-emerald-500/10 border-emerald-500/40" : highlight ? "bg-amber-500/10 border-amber-500/40" : "bg-card border-clay/40"}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-foreground/60 mt-0.5">{label}</div>
    </div>
  );
}

function QuickLink({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-3xl bg-card border-2 border-clay/40 hover:border-primary/60 p-4 transition-colors"
    >
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">{icon}</div>
      <div className="flex-1 min-w-1 flex items-center justify-between gap-2">
        <p className="font-bold text-sm">{label}</p>
        {!!badge && badge > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground">{badge}</span>
        )}
      </div>
    </Link>
  );
}
